import { Link, NavLink, useNavigate } from "react-router-dom";
import { House, MagnifyingGlass, List, X } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function Header() {
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    document.body.dataset.menu = open ? "open" : "closed";
  }, [open]);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#F9F8F6]/85 border-b border-[#E5E0D8]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#5B7B53] text-white">
            <House size={18} weight="duotone" />
          </span>
          <span className="font-display text-2xl font-semibold tracking-tight text-[#2C2B29]">
            HomeHearth
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 ml-4">
          <NavLink to="/" end className={({ isActive }) => `text-sm font-medium ${isActive ? "text-[#D17A58]" : "text-[#2C2B29] hover:text-[#5B7B53]"}`} data-testid="nav-home">Home</NavLink>
          <NavLink to="/lists" className={({ isActive }) => `text-sm font-medium ${isActive ? "text-[#D17A58]" : "text-[#2C2B29] hover:text-[#5B7B53]"}`} data-testid="nav-lists">Curated Lists</NavLink>
          <NavLink to="/compare" className={({ isActive }) => `text-sm font-medium ${isActive ? "text-[#D17A58]" : "text-[#2C2B29] hover:text-[#5B7B53]"}`} data-testid="nav-compare">Compare</NavLink>
          <NavLink to="/blog" className={({ isActive }) => `text-sm font-medium ${isActive ? "text-[#D17A58]" : "text-[#2C2B29] hover:text-[#5B7B53]"}`} data-testid="nav-blog">Blog</NavLink>
          <div className="relative group">
            <button data-testid="nav-categories" className="text-sm font-medium text-[#2C2B29] hover:text-[#5B7B53]">Categories</button>
            <div className="absolute left-0 top-full pt-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity">
              <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-[0_20px_48px_rgba(44,43,41,0.10)] p-2 w-64">
                {categories.map((c) => (
                  <Link
                    key={c.id}
                    to={`/category/${encodeURIComponent(c.name)}`}
                    data-testid={`cat-menu-${c.slug}`}
                    className="block px-3 py-2 rounded-lg text-sm text-[#2C2B29] hover:bg-[#EADDC8]/50 hover:text-[#5B7B53]"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <NavLink to="/disclosure" className={({ isActive }) => `text-sm font-medium ${isActive ? "text-[#D17A58]" : "text-[#2C2B29] hover:text-[#5B7B53]"}`} data-testid="nav-disclosure">Disclosure</NavLink>
        </nav>

        <form onSubmit={submit} className="hidden md:flex ml-auto items-center gap-2 relative">
          <MagnifyingGlass size={16} className="absolute left-3 text-[#5C5A56]" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products..."
            className="w-64 pl-9 pr-3 py-2 rounded-full border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#5B7B53]/30"
          />
        </form>
        {user ? (
          <Link to="/admin" data-testid="admin-link" className="hidden md:inline-flex text-sm font-medium text-[#5B7B53] hover:text-[#D17A58]">Admin</Link>
        ) : (
          <Link to="/admin/login" data-testid="admin-login-link" className="hidden md:inline-flex text-sm text-[#5C5A56] hover:text-[#2C2B29]">Sign in</Link>
        )}

        <button
          data-testid="mobile-menu-btn"
          className="md:hidden ml-auto p-2 text-[#2C2B29]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <List size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden absolute inset-x-0 top-full max-h-[calc(100dvh-72px)] overflow-y-auto border-t border-[#E5E0D8] bg-white px-6 py-4 space-y-3 shadow-[0_16px_32px_rgba(44,43,41,0.12)]">
          <form onSubmit={submit} className="flex items-center gap-2 relative">
            <MagnifyingGlass size={16} className="absolute left-3 text-[#5C5A56]" />
            <input
              data-testid="mobile-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-9 pr-3 py-2 rounded-full border border-[#E5E0D8] bg-[#F9F8F6] text-sm"
            />
          </form>
          <Link to="/" onClick={() => setOpen(false)} className="block py-2 text-[#2C2B29]">Home</Link>
          <Link to="/lists" onClick={() => setOpen(false)} className="block py-2 text-[#2C2B29]">Curated Lists</Link>
          <Link to="/compare" onClick={() => setOpen(false)} className="block py-2 text-[#2C2B29]" data-testid="mobile-nav-compare">Compare</Link>
          <Link to="/blog" onClick={() => setOpen(false)} className="block py-2 text-[#2C2B29]">Blog</Link>
          {categories.map((c) => (
            <Link key={c.id} to={`/category/${encodeURIComponent(c.name)}`} onClick={() => setOpen(false)} className="block py-2 text-[#2C2B29]">
              {c.name}
            </Link>
          ))}
          <Link to="/disclosure" onClick={() => setOpen(false)} className="block py-2 text-[#2C2B29]">Disclosure</Link>
          <Link to={user ? "/admin" : "/admin/login"} onClick={() => setOpen(false)} className="block py-2 text-[#5B7B53] font-medium">{user ? "Admin Dashboard" : "Sign in"}</Link>
        </div>
      )}
    </header>
  );
}
